/**
 * Verrekening van inkoopfacturen: welke aanbetaling staat al tegenover een definitieve factuur.
 *
 * Een pro forma is de aanbetaling. Hij is "verrekend" zodra hij aan een definitieve factuur hangt
 * (`linkedFinalInvoiceId`, met `linkedAt` als datum — allebei gezet bij het koppelen op de
 * detailpagina van de definitieve factuur). Een definitieve factuur wordt zelf nooit verrekend;
 * die neemt pro forma's op (`linkedProformaInvoiceIds`).
 *
 * Op één plek, omdat het facturenoverzicht (kolom, filter, sortering) en de Excel-export dezelfde
 * indeling moeten aanhouden — anders zegt het bestand iets anders dan het scherm.
 */

export const SETTLEMENT_STATES = {
  PAID_UNSETTLED: "paid_unsettled",
  UNPAID_UNSETTLED: "unpaid_unsettled",
  SETTLED: "settled",
  FINAL_WITH_PROFORMA: "final_with_proforma",
  FINAL_WITHOUT_PROFORMA: "final_without_proforma",
};

// Sorteervolgorde van de kolom Verrekening: betaald-maar-niet-verrekend is geld dat al weg is en
// nog nergens tegenover staat, dus dat komt bovenaan.
const SORT_ORDER = [
  SETTLEMENT_STATES.PAID_UNSETTLED,
  SETTLEMENT_STATES.UNPAID_UNSETTLED,
  SETTLEMENT_STATES.SETTLED,
  SETTLEMENT_STATES.FINAL_WITH_PROFORMA,
  SETTLEMENT_STATES.FINAL_WITHOUT_PROFORMA,
];

/**
 * Er is iets op betaald, ook als het nog niet het hele bedrag is: een deels betaalde aanbetaling is
 * net zo goed geld dat al weg is. De status is de terugval voor documenten zonder `totalPaid`.
 */
export const isInvoicePaid = (invoice) =>
  (invoice?.totalPaid || 0) >= 0.01 || invoice?.status === "paid" || invoice?.status === "partially_paid";

export const getSettlementState = (invoice) => {
  if (invoice?.type === "proforma") {
    if (invoice.linkedFinalInvoiceId) return SETTLEMENT_STATES.SETTLED;
    return isInvoicePaid(invoice) ? SETTLEMENT_STATES.PAID_UNSETTLED : SETTLEMENT_STATES.UNPAID_UNSETTLED;
  }
  return invoice?.linkedProformaInvoiceIds?.length
    ? SETTLEMENT_STATES.FINAL_WITH_PROFORMA
    : SETTLEMENT_STATES.FINAL_WITHOUT_PROFORMA;
};

export const compareSettlement = (a, b) =>
  SORT_ORDER.indexOf(getSettlementState(a)) - SORT_ORDER.indexOf(getSettlementState(b));

/** Dezelfde woorden als in de kolom op het overzicht. */
export const getSettlementLabel = (invoice) => {
  switch (getSettlementState(invoice)) {
    case SETTLEMENT_STATES.SETTLED:
      return "Verrekend";
    case SETTLEMENT_STATES.PAID_UNSETTLED:
    case SETTLEMENT_STATES.UNPAID_UNSETTLED:
      return "Nog niet verrekend";
    case SETTLEMENT_STATES.FINAL_WITH_PROFORMA: {
      const count = invoice.linkedProformaInvoiceIds.length;
      return `${count} pro forma${count > 1 ? "'s" : ""} verrekend`;
    }
    default:
      return "Geen aanbetaling gekoppeld";
  }
};

/**
 * De factuurnummers aan de andere kant van de verrekening: bij een pro forma de definitieve factuur,
 * bij een definitieve factuur de pro forma's. Een id dat niet (meer) in de lijst staat, krijgt de
 * eerste acht tekens, net als de link op het overzicht.
 */
export const getSettlementCounterparts = (invoice, invoicesById) => {
  const ids =
    invoice?.type === "proforma"
      ? [invoice.linkedFinalInvoiceId].filter(Boolean)
      : invoice?.linkedProformaInvoiceIds || [];
  return ids.map((id) => invoicesById?.get(id)?.invoiceNumber || id.substring(0, 8));
};

export const SETTLEMENT_FILTER_OPTIONS = [
  { value: "paid_unsettled", label: "Betaald, nog niet verrekend" },
  { value: "unsettled", label: "Nog niet verrekend" },
  { value: "settled", label: "Verrekend" },
];

/** Elk filter laat alleen pro forma's door; een onbekende waarde (oude localStorage) filtert niets. */
export const matchesSettlementFilter = (invoice, filter) => {
  const state = getSettlementState(invoice);
  switch (filter) {
    case "paid_unsettled":
      return state === SETTLEMENT_STATES.PAID_UNSETTLED;
    case "unsettled":
      return state === SETTLEMENT_STATES.PAID_UNSETTLED || state === SETTLEMENT_STATES.UNPAID_UNSETTLED;
    case "settled":
      return state === SETTLEMENT_STATES.SETTLED;
    default:
      return true;
  }
};
